import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';

import PropTypes from 'prop-types';

import API from '@/apiRequest';
import ListBackupComponent from '@/components/ListBackup/ListBackupComponent';
import { showToast } from '@/store/ducks/toasts';

const ListBackupContainer = ({ orderId, closeModal, refreshHandler }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastBackupUpdate, setLastBackupUpdate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestingBackup, setRequestingBackup] = useState(false);

  const dispatch = useDispatch();
  const intl = useIntl();
  const queryClient = useQueryClient();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await API.get(`orders/${orderId}/backups`);
      setLastBackupUpdate(response.data?.length ? response.data[0] : null);
    } catch (err) {
      console.error('Error fetching backups:', err?.response || err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestBackup = useCallback(async () => {
    try {
      setRequestingBackup(true);
      await API.post(`orders/${orderId}/backups/request`);
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS', defaultMessage: 'Sucesso' }),
          text: intl.formatMessage({
            id: 'list_backups_request_success',
            defaultMessage:
              'Solicitação enviada! O backup pode levar alguns minutos e depende do dispositivo estar ligado, com bateria e conectado à internet.',
          }),
          duration: 7000,
        })
      );
    } catch (err) {
      console.error('Error requesting backup:', err?.response || err);
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR', defaultMessage: 'Erro' }),
          text: intl.formatMessage({
            id: 'list_backups_request_error',
            defaultMessage: 'Não foi possível solicitar o backup do dispositivo.',
          }),
          duration: 5000,
        })
      );
    } finally {
      setRequestingBackup(false);
    }
  }, [dispatch, intl, orderId]);

  const handleSubmit = useCallback(async () => {
    if (!lastBackupUpdate) return;

    try {
      setSubmitting(true);
      setError(null);
      await API.post(`orders/${orderId}/backups/restore`, {
        code: lastBackupUpdate.code,
        device: lastBackupUpdate.device,
        device_id: lastBackupUpdate.device_id,
        datetime: lastBackupUpdate.datetime,
        data_size: lastBackupUpdate.data_size,
      });
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: intl.formatMessage({ id: 'list_backups_success' }),
          duration: 5000,
        })
      );
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (refreshHandler) refreshHandler();
      closeModal();
    } catch (err) {
      console.error('Error restoring backup:', err?.response || err);
      setError(
        err?.response?.status === 500 || !err?.response?.data?.message
          ? intl.formatMessage({ id: 'UNKNOWN_ERROR' })
          : intl.formatMessage({ id: err.response.data.message })
      );
    } finally {
      setSubmitting(false);
    }
  }, [closeModal, dispatch, intl, lastBackupUpdate, orderId, refreshHandler]);

  return (
    <ListBackupComponent
      error={error}
      handleSubmit={handleSubmit}
      handleClose={closeModal}
      handleRequestBackup={handleRequestBackup}
      intl={intl}
      lastBackupUpdate={lastBackupUpdate}
      loading={loading}
      requestingBackup={requestingBackup}
      submitting={submitting}
    />
  );
};

ListBackupContainer.propTypes = {
  orderId: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  refreshHandler: PropTypes.func.isRequired,
};

export default ListBackupContainer;
